import omit from 'lodash/omit';

import {TPeriodPickerValue} from '@edna/components';
import {TCurrency} from '@edna/utils';

import {TBadgeColor} from 'src/components';
import {TItemId} from 'src/constants';
import {EDocumentType, EResellerIds, TPaymentType} from 'src/containers/User';

import {TCreateAvailableTariffFormData} from '../Tariffs';

export enum ELeadClientStatus {
  ACCEPTED = 'ACCEPTED',
  DRAFT = 'DRAFT',
  REQUIRES_ATTENTION = 'REQUIRES_ATTENTION',
  WAITING = 'WAITING',
  CANCELLED = 'CANCELLED',
  ERROR = 'ERROR',
}

export enum ECountries {
  // Kazakhstan
  KAZ = 'KAZ',
  // Belarus
  BLR = 'BLR',
  // Russia
  RUS = 'RUS',
  // Indonesia
  IDN = 'IDN',
  // Azerbaijan
  AZE = 'AZE',
  // Armenia
  ARM = 'ARM',
  // Kyrgyzstan
  KGZ = 'KGZ',
  // Moldova
  MDA = 'MDA',
  // Tajikistan
  TJK = 'TJK',
  // Uzbekistan
  UZB = 'UZB',
  // Turkmenistan
  TKM = 'TKM',
  // Georgia
  GEO = 'GEO',
  // Abkhazia
  ABH = 'ABH',
  // South Ossetia
  OST = 'OST',
  // Ukraine
  UKR = 'UKR',
}

export const EU_COUNTRIES = [
  'AUT',
  'BEL',
  'BGR',
  'HUN',
  'DEU',
  'GRC',
  'DNK',
  'IRL',
  'ESP',
  'ITA',
  'CYP',
  'LVA',
  'LTU',
  'LUX',
  'MLT',
  'NLD',
  'POL',
  'PRT',
  'ROU',
  'SVK',
  'SVN',
  'FIN',
  'FRA',
  'HRV',
  'CZE',
  'SWE',
  'EST',
];

export enum ELeadClientCreatingError {
  TAX_NUMBER_ALREADY_IN_USE = 'TAX_NUMBER_ALREADY_IN_USE',
  EMAIL_ALREADY_IN_USE = 'EMAIL_ALREADY_IN_USE',
}

export enum ELeadType {
  CLIENT = 'CLIENT',
  AGENT = 'AGENT',
}

type TLeadCommonInfo = {
  id: number;
  reservedTenantId: number;
  createdAt: string;
  updatedAt: string;
  companyName: string;
  taxNumber: string;
  countryCode: string;
  currencyCode?: TCurrency;
  resellerId: EResellerIds;
  contactName: string;
  phone: string;
  email: string;
  responsibleManagerId: number;
  status: ELeadClientStatus;
  inviteUrl: string;
  inviteUrlCreatedAt: string;
  createdBy: number;
  locale: string;
  documentDate: string;
  documentNumber: string;
  selfRegistered?: boolean;
};

type TLeadClientInfo = {
  type: ELeadType.CLIENT;
  documentType: EDocumentType | null;
  activateLicenseDebt: boolean;
  paymentBankId?: number | null;
  billingDisabled?: boolean;
  paymentType: TPaymentType;
  managerResponsibleForCheckingId?: number | null;
  maxOverdraft: number;
  securityDeposit: number;
  licensePaymentTerm: number;
  termBeforeTerminationLicense: number;
  partnerId?: number | null;
  ogrn?: string;
  comment?: string;
};

export enum EAgentClientDocumentType {
  AGENT_AGREEMENT = 'AGENT_AGREEMENT',
  AGENT_CONTRACT = 'AGENT_CONTRACT',
}

export enum EAgentClientAccountingPeriod {
  MONTH = 'MONTH',
  QUARTER = 'QUARTER',
}

export const CUSTOM_TAX_RATE = 'CUSTOM';

export const TAX_RATE_OPTIONS: Partial<
  Record<EResellerIds, Record<EAgentClientDocumentType, (typeof CUSTOM_TAX_RATE | number)[]>>
> = {
  [EResellerIds.OSK_ID]: {
    [EAgentClientDocumentType.AGENT_CONTRACT]: [0, 0.05, 0.07, 0.2],
    [EAgentClientDocumentType.AGENT_AGREEMENT]: [0, 0.05, 0.07, 0.2],
  },
  [EResellerIds.KZ_ID]: {
    [EAgentClientDocumentType.AGENT_CONTRACT]: [0, 0.12, CUSTOM_TAX_RATE],
    [EAgentClientDocumentType.AGENT_AGREEMENT]: [0, 0.12, CUSTOM_TAX_RATE],
  },
  [EResellerIds.MFM_ID]: {
    [EAgentClientDocumentType.AGENT_CONTRACT]: [0, CUSTOM_TAX_RATE],
    [EAgentClientDocumentType.AGENT_AGREEMENT]: [0.19, CUSTOM_TAX_RATE],
  },
};

export enum EAgentClientSigningBasis {
  CHARTER = 'CHARTER', // Устав
  ENTRUSTMENT = 'ENTRUSTMENT', // Доверенность
  ORDER = 'ORDER', // Приказ
  CERTIFICATE = 'CERTIFICATE', // Свидетельство
}

type TAgentClientInfo = {
  type: ELeadType.AGENT;
  documentType: EAgentClientDocumentType;
  agentName: string;
  signatoryLastName: string;
  signatoryFirstName: string;
  signatoryMiddleName?: string;
  signingBasis: EAgentClientSigningBasis;
  signingBasisNumber?: string;
  signingBasisDate?: string;
  taxRate?: number;
  commissionPercentage?: number;
  accountingPeriod: EAgentClientAccountingPeriod;
};

export type TLeadClientAnalytics = Partial<Record<ELeadClientStatus, number>>;

export type TLead<T = ELeadType.CLIENT> = TLeadCommonInfo &
  (T extends ELeadType.AGENT ? TAgentClientInfo : TLeadClientInfo);

export type TLeadClient = TLead<ELeadType.CLIENT>;
export type TAgentClient = TLead<ELeadType.AGENT>;
export type TPartner = {agentName: string; tenantId: number};

export type TFormData = Partial<TLeadClient> & {
  hasPartner?: boolean;
  isChecked?: boolean;
  isAgreedOverdraft?: boolean;
  agreedOverdraft?: number;
  isDisableBillingAgreed?: boolean;
  isDocumentTypeApproved?: boolean;
  tariffs?: TCreateAvailableTariffFormData[];
};

export const DEFAULT_LEAD_CLIENT: TFormData = {
  companyName: '',
  contactName: '',
  countryCode: '',
  email: '',
  taxNumber: '',
  phone: '',
  isDocumentTypeApproved: false,
  documentNumber: '',
  documentDate: '',
  tariffs: [],
};

export type TAgentFormData = Omit<
  TAgentClient,
  | 'type'
  | 'createdBy'
  | 'status'
  | 'inviteUrl'
  | 'inviteUrlCreatedAt'
  | 'createdAt'
  | 'updatedAt'
  | 'signingBasisDate'
  | 'taxRate'
> & {
  signingBasisDate?: Date | null;
  taxRate?: number | typeof CUSTOM_TAX_RATE | null;
  customTaxRate?: number | null;
};

export const AGENT_CLIENT_STATUSES = omit(ELeadClientStatus, [
  ELeadClientStatus.DRAFT,
  ELeadClientStatus.REQUIRES_ATTENTION,
]);

export const DEFAULT_AGENT_CLIENT: Partial<TAgentFormData> = {
  documentType: EAgentClientDocumentType.AGENT_AGREEMENT,
  contactName: '',
  agentName: '',
  email: '',
  taxNumber: '',
  phone: '',
  signingBasisNumber: '',
  signingBasisDate: null,
};

export enum EFormStep {
  DETAILS = 'DETAILS',
  CALCULATION_AND_LICENSES = 'CALCULATION_AND_LICENSES',
  TARIFFS = 'TARIFFS',
}

export type TFormStep = keyof typeof EFormStep;

export enum ERegistrationType {
  SELF_SERVICE = 'SELF_SERVICE',
  ADMIN_CREATED = 'ADMIN_CREATED',
}

export type TFilters = {
  agentId?: TItemId;
  filter?: string;
  period?: TPeriodPickerValue | null;
  responsibleManagerId?: TItemId;
  resellerId?: TItemId;
  statuses?: Partial<Record<ELeadClientStatus, boolean>>;
  type?: ELeadType;
  registrationType?: Record<ERegistrationType, boolean>;
};

export const statusColorHash: Record<ELeadClientStatus, TBadgeColor> = {
  DRAFT: 'BLUE',
  WAITING: 'YELLOW',
  REQUIRES_ATTENTION: 'YELLOW',
  ACCEPTED: 'GREEN',
  ERROR: 'RED',
  CANCELLED: 'GREY',
};

export type TTenantLeadDetailsPayload<T = TAnyObject> = {
  supportTenantId?: TItemId;
  data: T;
};
